/**
 * Campaign Hierarchy Aggregation Utilities
 * Pure functions for aggregating metrics from ads → ad sets → channels
 */

import type {
  CampaignAdSetWithPerformance,
  CampaignChannelWithPerformance,
  PerformanceMetrics,
} from '@/types/campaign';

// Aggregate metrics from multiple entities
export function aggregateMetrics(
  entities: Array<{ metrics: PerformanceMetrics }>
): PerformanceMetrics {
  // Sum raw metrics
  const raw: PerformanceMetrics = {
    impressions: 0,
    clicks: 0,
    reach: 0,
    conversions: 0,
    cost: 0,
    revenue: 0,
    cpc: 0,
    ctr: 0,
    conversionRate: 0,
    roas: 0,
  };

  for (const entity of entities) {
    raw.impressions += entity.metrics.impressions || 0;
    raw.clicks += entity.metrics.clicks || 0;
    raw.reach = (raw.reach || 0) + (entity.metrics.reach || 0);
    raw.conversions += entity.metrics.conversions || 0;
    raw.cost += entity.metrics.cost || 0;
    raw.revenue += entity.metrics.revenue || 0;
  }

  // Calculate derived metrics
  // Cost per CLICK. It divided by CONVERSIONS, which made the demo channel's
  // CPC ฿652 instead of ฿11.54 on ฿60,000 / 5,200 clicks — 56x, in the mode
  // shown to people evaluating the product. Every other cpc in this codebase
  // is cost / clicks; this one was alone and its test was named after the bug.
  raw.cpc = raw.clicks > 0 ? raw.cost / raw.clicks : 0;
  raw.ctr = raw.impressions > 0 ? (raw.clicks / raw.impressions) * 100 : 0;
  raw.conversionRate = raw.clicks > 0 ? (raw.conversions / raw.clicks) * 100 : 0;
  // null when no revenue came back — see mediaIntelligenceService for why 0 lies here.
  raw.roas = raw.cost > 0 && raw.revenue > 0 ? raw.revenue / raw.cost : null;

  return raw;
}

// Aggregate ad set metrics from ads
export function aggregateAdSetMetrics(
  adSet: CampaignAdSetWithPerformance
): CampaignAdSetWithPerformance {
  const adsMetrics = adSet.ads.map((ad) => ({ metrics: ad }));
  const aggregated = aggregateMetrics(adsMetrics);
  return { ...adSet, ...aggregated };
}

// Aggregate channel metrics from ad sets
export function aggregateChannelMetrics(
  channel: CampaignChannelWithPerformance
): CampaignChannelWithPerformance {
  const adSetsToAggregate = channel.adSets || [];
  const aggregatedAdSets = adSetsToAggregate.map(aggregateAdSetMetrics);
  const adSetsMetrics = aggregatedAdSets.map((adSet) => ({ metrics: adSet }));
  const aggregated = aggregateMetrics(adSetsMetrics);
  return { ...channel, adSets: aggregatedAdSets, ...aggregated };
}
